import React, { useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { getSourceHistory } from "../api";
import SourceHistoryDrawer from "./SourceHistoryDrawer";
import { relativeTime } from "./SourceHistoryTimeline";

const ACTION_PAST_TENSE = {
  CREATE: "Created",
  UPDATE: "Modified",
  ARCHIVE: "Archived",
  RESTORE: "Restored",
};

// Compact audit signal shown near the source name. Answers the most common audit question
// finance asks ("who last touched this, and when?") in a single line that costs almost no
// real estate — the full timeline is one click away in the drawer. Renders nothing until
// the source has any recorded history, so create mode and new sources stay clean.
const SourceLastModifiedLine = ({ sourceId, enabled, valueLabels }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data } = useQuery(
    ["source-history", sourceId],
    () => getSourceHistory(sourceId),
    { enabled: enabled && sourceId != null },
  );
  const entries = data?.entries || [];
  const latest = entries[0];
  // Prefer the backend-resolved display name; fall back to the raw changed_by so the line
  // still says *something* attributable when the user lookup misses (deleted user, etc.).
  const actor = latest && (latest.actor_display || latest.changed_by);

  if (!enabled || sourceId == null) return null;

  return (
    <>
      {latest && (
        <p className="mt-1 text-[11px] text-text-2">
          {ACTION_PAST_TENSE[latest.change_type] || "Modified"}{" "}
          <span title={latest.changed_at}>{relativeTime(latest.changed_at)}</span>
          {actor && (
            <>
              {" "}by <span className="text-text">{actor}</span>
            </>
          )}
          {" · "}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="text-primary hover:text-primary-hover underline underline-offset-2"
          >
            View history
          </button>
        </p>
      )}
      <SourceHistoryDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sourceId={sourceId}
        valueLabels={valueLabels}
      />
    </>
  );
};

SourceLastModifiedLine.propTypes = {
  // Source id whose history to summarise. Renders nothing when missing.
  sourceId: PropTypes.number,
  // Render gate — false in create mode (no audit trail to show) or in advanced / aiOnly states.
  enabled: PropTypes.bool,
  // Optional project-id → friendly-name map. Threaded into the drawer so the audit diffs
  // can render "InstantViewAI" rather than a raw project UUID. Not needed for the inline
  // line itself, which only shows actor + timestamp.
  valueLabels: PropTypes.object,
};

export default SourceLastModifiedLine;
